import { type FlowStep, type SplitStep, type SMSStep, type EmailStep, type DelayStep } from '@spike/automation-model';

const flatten = (steps: FlowStep[]): FlowStep[] =>
    steps.flatMap(step =>
        step.type === 'split'
            ? [step, ...flatten((step as SplitStep).branches.flatMap(b => b.steps) as FlowStep[])]
            : [step]
    );

const describe = (step: FlowStep, fallback: string): string => step.label?.trim() || fallback;

/**
 * Every reason this flow cannot be saved yet, so the Save tooltip can list them
 * all instead of revealing them one at a time.
 */
export const validateFlow = (steps: FlowStep[]): string[] => {
    const all = flatten(steps);
    const problems: string[] = [];

    if (all.filter(s => s.type === 'sms' || s.type === 'email').length === 0) {
        problems.push('There is no SMS or email step yet — an automation has to end in a message.');
    }

    all.forEach(step => {
        if (step.type === 'sms') {
            const sms = step as SMSStep;
            if (!sms.content?.message?.trim()) {
                problems.push(`${describe(step, 'The SMS step')}: the message body is empty.`);
            }
        }

        if (step.type === 'email') {
            const email = step as EmailStep;
            if (!email.subjectLine?.trim()) {
                problems.push(`${describe(step, 'The email step')}: the subject line is empty.`);
            }
            if (email.emailTemplateId == null) {
                problems.push(`${describe(step, 'The email step')}: no template has been selected.`);
            }
        }

        if (step.type === 'delay') {
            const delay = step as DelayStep;
            if (!delay.amount || delay.amount < 1) {
                problems.push(`${describe(step, 'The delay step')}: the wait time has to be at least 1.`);
            }
        }

        if (step.type === 'split') {
            const split = step as SplitStep;
            if (split.conditions.length === 0) {
                problems.push(
                    `${describe(
                        step,
                        'The split step'
                    )}: add at least one condition, otherwise every client takes the Yes branch.`
                );
            }
            if (split.branches.every(branch => branch.steps.length === 0)) {
                problems.push(`${describe(step, 'The split step')}: neither branch has a step to run.`);
            }
        }
    });

    return problems;
};

export default validateFlow;
